import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import {
  Upload,
  ScanText,
  Languages,
  FileText,
  FileSearch,
  Clock,
  AlertTriangle,
  Loader2,
  Download,
  Save,
  Eye,
  Plus,
  X,
  CheckCircle,
  Info,
} from 'lucide-react';
import { isDesktop } from '@/lib/fileSystem';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { addOcrTextLayers, ocrPageNumbers, ocrTextContent } from '@/lib/ocrDocument';
import { formatFileSize } from '@/lib/pdfOperations';
import { ocrImage as nativeOCR, getOCRLanguages } from '@/lib/nativebridge';
import Tesseract from 'tesseract.js';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

// ──────────── Language options ────────────
const COMMON_LANGUAGES = [
  { code: 'eng', label: 'English' },
  { code: 'spa', label: 'Spanish' },
  { code: 'fra', label: 'French' },
  { code: 'deu', label: 'German' },
  { code: 'ita', label: 'Italian' },
  { code: 'por', label: 'Portuguese' },
  { code: 'nld', label: 'Dutch' },
  { code: 'chi_sim', label: 'Chinese (Simplified)' },
  { code: 'chi_tra', label: 'Chinese (Traditional)' },
  { code: 'jpn', label: 'Japanese' },
  { code: 'kor', label: 'Korean' },
  { code: 'ara', label: 'Arabic' },
  { code: 'rus', label: 'Russian' },
  { code: 'hin', label: 'Hindi' },
  { code: 'pol', label: 'Polish' },
  { code: 'tur', label: 'Turkish' },
  { code: 'vie', label: 'Vietnamese' },
  { code: 'swe', label: 'Swedish' },
  { code: 'dan', label: 'Danish' },
  { code: 'tha', label: 'Thai' },
  { code: 'tam', label: 'Tamil' },
];
const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
const languageLabels = new Map(COMMON_LANGUAGES.map(({ code, label }) => [code, label]));
const languageOverrides = { aze_cyrl: 'Azerbaijani (Cyrillic)', ita_old: 'Italian (Old)', kat_old: 'Georgian (Old)', spa_old: 'Spanish (Old)', srp_latn: 'Serbian (Latin)', uzb_cyrl: 'Uzbek (Cyrillic)', frk: 'German (Fraktur)' };
// Use the installed OCR engine's catalog. Previously the web exposed only
// 21 languages, and desktop replaced those with just its installed packs.
const LANGUAGES = Object.values(Tesseract.languages).map((code) => {
  let label = languageLabels.get(code) || languageOverrides[code];
  if (!label) { try { label = languageNames.of(code); } catch { label = code; } }
  return { code, label };
}).sort((a, b) => a.label.localeCompare(b.label));

// ──────────── OCR quality presets ────────────
const ACCURACY_LEVELS = [
  { id: 'fast', label: 'Fast', description: 'Faster processing, lower accuracy' },
  { id: 'balanced', label: 'Balanced', description: 'Good tradeoff (default)' },
  { id: 'best', label: 'Best', description: 'Slowest, highest accuracy' },
];

const PSM_OPTIONS = [
  { value: '3', label: 'Auto (default)' },
  { value: '6', label: 'Single Block' },
  { value: '4', label: 'Single Column' },
  { value: '7', label: 'Single Line' },
];

const OUTPUT_MODES = [
  { id: 'searchable', label: 'Searchable PDF', icon: FileSearch, description: 'Adds invisible text layer — looks identical but text is selectable' },
  { id: 'text', label: 'Text Only', icon: FileText, description: 'Extracts text as a .txt file' },
  { id: 'both', label: 'PDF + Text', icon: FileSearch, description: 'Searchable PDF and a separate .txt file' },
];

const PAGE_SCOPE_OPTIONS = [
  { id: 'all', label: 'All pages' },
  { id: 'custom', label: 'Custom range' },
  { id: 'notext', label: 'Only pages without text' },
];

// Tesseract OEM modes: 0=Legacy, 1=LSTM, 2=Legacy+LSTM, 3=Default
const DPI_MAP = { fast: 150, balanced: 225, best: 300 };

/**
 * OCR — Convert scanned PDFs into searchable, selectable text using Tesseract.js.
 */
export default function OCR() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile, saveFile: saveFileDialog } = useFileDialog();
  // CloudSourceHint wires into loadFile defined below.

  // ── File state ──
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // ── Scan detection ──
  const [scanStatus, setScanStatus] = useState(null); // 'scanned' | 'has-text' | null
  const [detectingText, setDetectingText] = useState(false);
  const [pagesWithText, setPagesWithText] = useState(new Set()); // pages that already have text

  // ── Options ──
  const [primaryLang, setPrimaryLang] = useState('eng');
  const [secondaryLang, setSecondaryLang] = useState(null);
  const [showSecondaryLang, setShowSecondaryLang] = useState(false);
  const [outputMode, setOutputMode] = useState('searchable');
  const [accuracy, setAccuracy] = useState('balanced');
  const [psm, setPsm] = useState('3');
  const [pageScope, setPageScope] = useState('notext');
  const [customRange, setCustomRange] = useState('');

  // ── Language download ──
  const [langDownloading, setLangDownloading] = useState(false);
  const [langDownloadProgress, setLangDownloadProgress] = useState(0);

  // ── Processing state ──
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPagesToProcess, setTotalPagesToProcess] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState(null);
  const [liveText, setLiveText] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const cancelledRef = useRef(false);
  const [error, setError] = useState(null);

  // ── Results ──
  const [result, setResult] = useState(null);
  // { pdfBlob, textContent, totalPages, totalWords, avgConfidence, processingTime }
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // ── Native language packs ──
  const [nativeLanguages, setNativeLanguages] = useState([]);

  useEffect(() => {
    getOCRLanguages().then((langs) => {
      if (langs && langs.length > 0) setNativeLanguages(langs);
    }).catch(() => {});
  }, []);

  // ==================== File handling ====================

  const detectScannedPages = useCallback(async (doc) => {
    setDetectingText(true);
    const pagesToCheck = Math.min(doc.numPages, 5);
    let totalChars = 0;
    const withText = new Set();

    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map((item) => item.str).join('');
      if (pageText.trim().length > 0) {
        withText.add(i);
      }
      if (i <= pagesToCheck) {
        totalChars += pageText.trim().length;
      }
    }

    setPagesWithText(withText);
    // If first few pages have very little text, likely scanned
    const avgChars = totalChars / pagesToCheck;
    setScanStatus(avgChars < 20 ? 'scanned' : 'has-text');
    setDetectingText(false);
  }, []);

  const loadFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;

    setError(null);
    setResult(null);
    setScanStatus(null);
    setPagesWithText(new Set());
    setLiveText('');
    setFileName(file.name);
    setFileSize(file.size);

    try {
      // Decrypt owner-password PDFs (lossless) / reject truly locked ones
      // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
      const buf = await readPdfLibBytes(file);
      setArrayBuffer(buf);
      const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setPdfDoc(doc);
      rememberDocument(file);
      setNumPages(doc.numPages);
      await detectScannedPages(doc);
    } catch (err) {
      setDetectingText(false);
      setError(pdfIntakeErrorMessage(err, 'run OCR'));
    }
  }, [detectScannedPages, rememberDocument]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (!result) return;
    const file = new File([result.buffer], result.name, { type: 'application/pdf' });
    loadFile(file);
  }, [openFile, loadFile]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  // ==================== Page range parsing ====================

  const getPageNumbers = useCallback(() => ocrPageNumbers(pageScope, customRange, numPages, pagesWithText),
    [pageScope, customRange, numPages, pagesWithText]);

  const workerRef = useRef(null);
  useEffect(() => () => {
    cancelledRef.current = true;
    workerRef.current?.terminate();
  }, []);

  const handleOCR = useCallback(async () => {
    if (!pdfDoc || !arrayBuffer || processing) return;
    setError(null);
    let pageNumbers;
    try { pageNumbers = getPageNumbers(); }
    catch (err) { setError(err.message); return; }
    if (!pageNumbers.length) {
      setError('These pages already contain text. Choose All pages or a custom range to recognize them again.');
      return;
    }
    setProcessing(true);
    setResult(null);
    setLiveText('');
    setCancelled(false);
    cancelledRef.current = false;
    setTotalPagesToProcess(pageNumbers.length);
    setCurrentPage(0);
    setOverallProgress(0);
    const startTime = Date.now();
    const language = secondaryLang ? `${primaryLang}+${secondaryLang}` : primaryLang;
    const dpi = DPI_MAP[accuracy];
    let worker = null;
    let useNative = true;
    try {
      const results = [];
      let allText = '';
      const confidenceScores = [];
      for (const [index, pageNum] of pageNumbers.entries()) {
        if (cancelledRef.current) return;
        setCurrentPage(pageNum);
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: dpi / 72 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        let pdfBytes = null;
        let text = '';
        if (useNative) {
          const png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          try { pdfBytes = await nativeOCR(await png.arrayBuffer(), language, { localOnly: true, dpi, psm }); }
          catch { useNative = false; }
          if (!pdfBytes) useNative = false;
        }
        if (cancelledRef.current) return;
        if (pdfBytes) {
          const recognized = await pdfjsLib.getDocument({ data: new Uint8Array(pdfBytes).slice() }).promise;
          try {
            const page = await recognized.getPage(1);
            text = ocrTextContent((await page.getTextContent()).items);
          } finally { await recognized.destroy(); }
        } else {
          if (!worker) {
            // Current traineddata is LSTM-only; OEM 0 (legacy) fails to load.
            worker = await Tesseract.createWorker(language, 1, {
              logger: (message) => {
                if (cancelledRef.current) return;
                setLangDownloading(message.status?.includes('loading') || false);
                setLangDownloadProgress(message.progress || 0);
              },
            });
            workerRef.current = worker;
            await worker.setParameters({ tessedit_pageseg_mode: psm, user_defined_dpi: String(dpi) });
          }
          if (cancelledRef.current) return;
          // v7 no longer supplies data.words by default. Its PDF renderer
          // supplies correct Unicode glyphs/positioning, unlike hand-drawn
          // Helvetica words that overlap and silently omit other alphabets.
          const { data } = await worker.recognize(canvas, { pdfTextOnly: true }, { text: true, pdf: true });
          if (!data.pdf) throw new Error('The OCR engine did not return a searchable text layer.');
          pdfBytes = new Uint8Array(data.pdf);
          text = data.text || '';
          if (Number.isFinite(data.confidence)) confidenceScores.push(data.confidence);
        }
        canvas.width = 0;
        canvas.height = 0;
        if (cancelledRef.current) return;
        results.push({ pageNum, pdfBytes });
        allText += `--- Page ${pageNum} ---\n${text.trim()}\n\n`;
        setLiveText(allText);
        setOverallProgress((index + 1) / pageNumbers.length);
        setEstimatedTimeLeft(Math.round((Date.now() - startTime) / (index + 1) * (pageNumbers.length - index - 1) / 1000));
      }
      const pdfBlob = outputMode === 'text' ? null : new Blob([await addOcrTextLayers(arrayBuffer, results)], { type: 'application/pdf' });
      if (cancelledRef.current) return;
      const recognizedWords = allText.replace(/--- Page \d+ ---/g, '').trim();
      setResult({ pdfBlob, textContent: allText, totalPages: results.length,
        totalWords: recognizedWords ? recognizedWords.split(/\s+/).length : 0,
        avgConfidence: confidenceScores.length ? Math.round(confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length) : null,
        processingTime: Math.round((Date.now() - startTime) / 1000) });
    } catch (err) {
      if (!cancelledRef.current) setError(`OCR failed: ${err.message}`);
    } finally {
      if (worker) await worker.terminate();
      workerRef.current = null;
      setProcessing(false);
      setEstimatedTimeLeft(null);
      setLangDownloading(false);
    }
  }, [pdfDoc, arrayBuffer, processing, getPageNumbers, primaryLang, secondaryLang, accuracy, psm, outputMode]);

  const handleCancel = useCallback(() => {
    setCancelled(true);
    cancelledRef.current = true;
  }, []);

  // ==================== Download handlers ====================

  const downloadPDF = useCallback(async () => {
    if (!result?.pdfBlob) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    const baseName = fileName.replace(/\.pdf$/i, '');
    await saveFileDialog(result.pdfBlob, `${baseName}_searchable.pdf`);
    if (usageCapApplies()) incrementUsage();
  }, [result, fileName, saveFileDialog]);

  const downloadText = useCallback(async () => {
    if (!result?.textContent) return;
    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }
    const baseName = fileName.replace(/\.pdf$/i, '');
    const blob = new Blob([result.textContent], { type: 'text/plain' });
    await saveFileDialog(blob, `${baseName}_ocr.txt`);
    if (usageCapApplies()) incrementUsage();
  }, [result, fileName, saveFileDialog]);

  const openInViewer = useCallback(() => {
    if (!result?.pdfBlob) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.pdfBlob], `${baseName}_searchable.pdf`, { type: 'application/pdf' });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate]);

  // ==================== Derived values ====================

  let pagesToProcess = [];
  let rangeError = null;
  try { if (pdfDoc) pagesToProcess = getPageNumbers(); }
  catch (err) { rangeError = err.message; }

  // ==================== Render ====================

  return (
    <ProGate feature="ocr">
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">OCR — Text Recognition</h1>
          <p className="text-sm text-gray-500">Convert scanned PDFs to searchable text. Recognition runs on this device.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        {/* Drop zone */}
        {!pdfDoc && (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging
                  ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
                  : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Scanned or image-based PDFs work best</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        )}

        {pdfDoc && !result && (
          <>
            {/* File info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-3">
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}
                </p>
              </div>
              <button
                onClick={handleBrowse}
                className="text-sm text-brand-blue hover:underline font-medium"
              >
                Change file
              </button>
            </div>

            {/* Scan detection message */}
            {detectingText && (
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                <Loader2 size={14} className="animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Analyzing document…</span>
              </div>
            )}
            {scanStatus === 'scanned' && (
              <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <Info size={14} className="shrink-0 text-amber-500 mt-0.5" />
                <span className="text-sm text-amber-700">
                  This PDF appears to be scanned/image-based — OCR can make it searchable.
                </span>
              </div>
            )}
            {scanStatus === 'has-text' && (
              <div className="flex items-start gap-2 px-4 py-3 bg-brand-light border border-brand-muted/30 rounded-lg mb-4">
                <Info size={14} className="shrink-0 text-brand-blue mt-0.5" />
                <span className="text-sm text-brand-dark">
                  This PDF already contains selectable text. OCR may not be needed, but you can still run it to improve accuracy or add a text layer to image-heavy pages.
                </span>
              </div>
            )}

            {/* Options panel */}
            {!processing && (
              <div className="space-y-4 mb-6">
                {/* Language selection */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Languages size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900">Language</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      aria-label="OCR language"
                      value={primaryLang}
                      onChange={(e) => setPrimaryLang(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    >
                      {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}{nativeLanguages.includes(l.code) ? ' · installed' : ''}</option>)}
                    </select>

                    {showSecondaryLang ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-xs text-gray-400">+</span>
                        <select
                          aria-label="Second OCR language"
                          value={secondaryLang || ''}
                          onChange={(e) => setSecondaryLang(e.target.value || null)}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                        >
                          <option value="">Select...</option>
                          {LANGUAGES.filter((l) => l.code !== primaryLang).map((l) => <option key={l.code} value={l.code}>{l.label}{nativeLanguages.includes(l.code) ? ' · installed' : ''}</option>)}
                        </select>
                        <button
                          onClick={() => { setShowSecondaryLang(false); setSecondaryLang(null); }}
                          className="p-1 rounded hover:bg-gray-100 text-gray-400"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSecondaryLang(true)}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-brand-blue hover:bg-brand-light/30 rounded-lg transition-colors"
                      >
                        <Plus size={14} />
                        Add language
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Installed language packs work offline. Other languages need an internet connection on first use to download language data. Your pages stay on this device.
                  </p>
                </div>

                {/* Output mode */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Output Mode</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {OUTPUT_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setOutputMode(mode.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          outputMode === mode.id
                            ? 'border-brand-blue bg-brand-light/20'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <mode.icon
                          size={18}
                          className={outputMode === mode.id ? 'text-brand-blue mb-1.5' : 'text-gray-400 mb-1.5'}
                        />
                        <p className="text-xs font-semibold text-gray-900">{mode.label}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{mode.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* OCR quality */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">OCR Quality</h3>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {ACCURACY_LEVELS.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => setAccuracy(level.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          accuracy === level.id
                            ? 'border-brand-blue bg-brand-light/20'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="text-xs font-semibold text-gray-900">{level.label}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{level.description}</p>
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Page Segmentation</label>
                    <select
                      value={psm}
                      onChange={(e) => setPsm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    >
                      {PSM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Page scope */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Page Scope</h3>
                  <div className="flex flex-wrap gap-2">
                    {PAGE_SCOPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setPageScope(opt.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          pageScope === opt.id
                            ? 'border-brand-blue bg-brand-light/20 text-brand-blue'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {pageScope === 'custom' && (
                    <input
                      type="text"
                      value={customRange}
                      onChange={(e) => setCustomRange(e.target.value)}
                      placeholder="e.g. 1-3, 5, 7-9"
                      className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                    />
                  )}
                  {pageScope === 'notext' && pagesWithText.size > 0 && (
                    <p className="mt-2 text-xs text-gray-500">
                      {pagesWithText.size} page{pagesWithText.size !== 1 ? 's' : ''} already have text and will be skipped.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Processing section */}
            {!processing && !result && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {(error || rangeError) && <p role="alert" className="text-red-600 text-sm mb-4">{error || rangeError}</p>}

                <button
                  onClick={handleOCR}
                  disabled={detectingText || !pagesToProcess.length}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                >
                  <ScanText size={18} />
                  Process {pagesToProcess.length} Page{pagesToProcess.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}

            {/* Processing progress */}
            {processing && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {/* Language download indicator */}
                {langDownloading && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Loader2 size={14} className="animate-spin text-brand-blue" />
                      <span className="text-sm text-gray-600">Downloading language data…</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-blue/50 rounded-full transition-all duration-200"
                        style={{ width: `${langDownloadProgress * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Per-page progress */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-brand-blue" />
                    <span className="text-sm text-gray-700">
                      Processing page {currentPage} of {numPages}
                    </span>
                  </div>
                  {estimatedTimeLeft !== null && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock size={12} />
                      ~{estimatedTimeLeft}s remaining
                    </div>
                  )}
                </div>

                {/* Overall progress bar */}
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-brand-blue rounded-full transition-all duration-300"
                    style={{ width: `${overallProgress * 100}%` }}
                  />
                </div>

                {/* Live text preview */}
                {liveText && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-1.5">Recognized text preview:</p>
                    <div className="max-h-40 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 font-mono whitespace-pre-wrap">
                      {liveText}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCancel}
                  className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}

        {/* Results */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="text-center mb-6">
              <CheckCircle size={40} className="mx-auto mb-3 text-green-500" />
              <p className="text-lg font-semibold text-gray-900">OCR Complete</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-gray-900">{result.totalPages}</p>
                <p className="text-[10px] text-gray-500">Pages</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-gray-900">{result.totalWords.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500">Words</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className={`text-lg font-bold ${result.avgConfidence == null || result.avgConfidence >= 70 ? 'text-gray-900' : 'text-amber-600'}`}>
                  {result.avgConfidence == null ? '—' : `${result.avgConfidence}%`}
                </p>
                <p className="text-[10px] text-gray-500">Confidence</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-gray-900">{result.processingTime}s</p>
                <p className="text-[10px] text-gray-500">Time</p>
              </div>
            </div>

            {/* Low confidence warning */}
            {result.avgConfidence != null && result.avgConfidence < 70 && (
              <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <AlertTriangle size={14} className="shrink-0 text-amber-500 mt-0.5" />
                <span className="text-sm text-amber-700">
                  Low confidence — results may contain errors. Try selecting a different language or using the Best accuracy setting.
                </span>
              </div>
            )}

            {/* Download buttons */}
            <div className="flex gap-3 mb-3">
              {result.pdfBlob && (
                <>
                  <button
                    onClick={openInViewer}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
                  >
                    <Eye size={16} />
                    Open in Viewer
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    {isDesktop()
                      ? (<><Save size={16} />Save PDF</>)
                      : (<><Download size={16} />Download PDF</>)}
                  </button>
                </>
              )}
              {result.textContent && (
                <button
                  onClick={downloadText}
                  className={`flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors ${
                    !result.pdfBlob ? 'flex-1' : ''
                  }`}
                >
                  <FileText size={16} />
                  {isDesktop() ? 'Save Text' : 'Download Text'}
                </button>
              )}
            </div>

            {/* Live text from processing */}
            {liveText && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Extracted text:</p>
                <div className="max-h-48 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 font-mono whitespace-pre-wrap">
                  {liveText}
                </div>
              </div>
            )}

            <button
              onClick={() => { setResult(null); setLiveText(''); }}
              className="w-full mt-1 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
            >
              Process another file
            </button>

          </div>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}
